import * as React from 'react';
import { Autocomplete, Box, Button, Stack, TextField, Typography, createFilterOptions } from '@mui/material';
import ReactCountryFlag from 'react-country-flag';
import HomeWorkRoundedIcon from '@mui/icons-material/HomeWorkRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import LocationCityRoundedIcon from '@mui/icons-material/LocationCityRounded';
import { useTranslation } from 'react-i18next';
import SwitchComponent from '../switch/SwitchComponent';
import { REMOTE_NATION, isRemoteNation } from '../../common/hooks/useReferenceData';
import { regionName } from '../../utils/utils';

export const nationLabel = (nation, lang, t) => {
  if (!nation) return '';
  if (isRemoteNation(nation)) return t('postDetails.remote');
  return regionName(nation.nationTag, lang, nation.nationName) || nation.nationName || '';
};

export const sameNation = (a, b) => {
  if (!a || !b) return false;
  if (isRemoteNation(a) || isRemoteNation(b)) return isRemoteNation(a) && isRemoteNation(b);
  if (a.nationId && b.nationId) return String(a.nationId) === String(b.nationId);
  return String(a.nationTag || '').toLowerCase() === String(b.nationTag || '').toLowerCase();
};

const SectionLabel = ({ children }) => (
  <Typography variant="overline" sx={{ display: 'block', color: 'text.secondary', fontWeight: 800, letterSpacing: '0.08em', mb: 1, lineHeight: 1.6 }}>
    {children}
  </Typography>
);

/**
 * Filters for the job list. Every change is applied immediately through onChange(partialFilters).
 * value: { nation, country, seek, withDescription }
 */
export default function SearchComponent({ nations = [], countries = [], value, onChange, onReset, activeCount = 0, showDescriptionToggle = true }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || i18n.language || 'en';

  const nationOptions = React.useMemo(() => {
    const remote = { ...REMOTE_NATION, nationName: t('postDetails.remote') };
    const sorted = [...nations]
      .filter((n) => n && !isRemoteNation(n))
      .map((n) => ({ ...n, _label: nationLabel(n, lang, t) }))
      .sort((a, b) => String(a.continent || '').localeCompare(String(b.continent || '')) || a._label.localeCompare(b._label, lang));
    return [{ ...remote, _label: remote.nationName }, ...sorted];
  }, [nations, lang, t]);

  const filterNations = React.useMemo(
    () => createFilterOptions({ stringify: (o) => `${o._label} ${o.nationName || ''} ${o.nationTag || ''}` }),
    []
  );

  const selectedNation = value.nation ? nationOptions.find((o) => sameNation(o, value.nation)) || { ...value.nation, _label: nationLabel(value.nation, lang, t) } : null;
  const remoteSelected = isRemoteNation(value.nation);

  return (
    <Stack spacing={3}>
      <Box>
        <SectionLabel>{t('jobs.jobType')}</SectionLabel>
        <SwitchComponent
          checked={Boolean(value.seek)}
          handleSwitchSelected={(checked) => onChange({ seek: checked })}
          labelLeft={t('jobs.offers')}
          labelRight={t('jobs.seek')}
        />
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 1 }}>
          {value.seek ? t('jobs.seekHint') : t('jobs.offersHint')}
        </Typography>
      </Box>

      <Box>
        <SectionLabel>{t('jobs.location')}</SectionLabel>
        <Stack spacing={1.5}>
          <Autocomplete
            options={nationOptions}
            value={selectedNation}
            onChange={(e, newValue) => onChange({ nation: newValue || null, country: null })}
            getOptionLabel={(o) => o?._label || nationLabel(o, lang, t)}
            filterOptions={filterNations}
            groupBy={(o) => (isRemoteNation(o) ? t('jobs.workMode') : t(`continents.${o.continent}`, o.continent || ''))}
            isOptionEqualToValue={sameNation}
            autoHighlight
            renderOption={(props, option) => {
              const { key, ...rest } = props;
              return (
                <Box component="li" key={`${option.nationId}-${option.nationTag}`} {...rest} sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  {isRemoteNation(option) ? (
                    <HomeWorkRoundedIcon fontSize="small" sx={{ color: '#059669' }} />
                  ) : (
                    <ReactCountryFlag countryCode={option.nationTag} svg style={{ width: '1.3em', height: '1.3em', borderRadius: 3 }} aria-hidden="true" />
                  )}
                  <span>{option._label}</span>
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('jobs.searchNation')}
                placeholder={t('jobs.typeToSearch')}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      {selectedNation && !remoteSelected && selectedNation.nationTag ? (
                        <ReactCountryFlag countryCode={selectedNation.nationTag} svg style={{ width: '1.3em', height: '1.3em', marginLeft: 6, borderRadius: 3 }} aria-hidden="true" />
                      ) : (
                        <PublicRoundedIcon sx={{ color: 'text.secondary', ml: 0.75 }} fontSize="small" />
                      )}
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Autocomplete
            options={countries}
            value={value.country || null}
            onChange={(e, newValue) => onChange({ country: newValue || null })}
            getOptionLabel={(o) => o?.countryName || ''}
            isOptionEqualToValue={(o, v) => String(o.countryId) === String(v?.countryId)}
            disabled={!value.nation || remoteSelected || countries.length === 0}
            autoHighlight
            renderOption={(props, option) => {
              const { key, ...rest } = props;
              return <li key={option.countryId} {...rest}>{option.countryName}</li>;
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('jobs.searchCountry')}
                placeholder={value.nation && !remoteSelected ? t('jobs.typeToSearch') : t('jobs.selectNationFirst')}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <LocationCityRoundedIcon sx={{ color: 'text.secondary', ml: 0.75 }} fontSize="small" />
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Stack>
      </Box>

      {showDescriptionToggle && (
        <Box>
          <SectionLabel>{t('jobs.includeDescription')}</SectionLabel>
          <SwitchComponent
            checked={Boolean(value.withDescription)}
            handleSwitchSelected={(checked) => onChange({ withDescription: checked })}
            labelLeft={t('jobs.onlyTitle')}
            labelRight={t('jobs.alsoDescription')}
            size="small"
          />
        </Box>
      )}

      <Button
        variant="outlined"
        color="inherit"
        startIcon={<RestartAltRoundedIcon />}
        onClick={onReset}
        disabled={activeCount === 0}
        sx={{ borderColor: 'divider', color: 'text.secondary' }}
      >
        {t('jobs.resetFilter')}
      </Button>
    </Stack>
  );
}
